import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { runFullScheduling } from '@nsc/workflows-dell/scheduling/api/run-full-scheduling.js';
import { runFullSimulatedScheduling } from '@nsc/workflows-dell/scheduling/api/run-full-simulated-scheduling.js';
import {
  runManualChecksum,
  runOutboundPudoPickup,
  runSendEmailPartExchange,
  waitForManualChecksumToFinish,
} from '@nsc/workflows-dell/scheduling/api/run-manual-checksum.js';
import {
  clearSchedulingState,
  subscribeSchedulingState,
} from '@nsc/workflows-dell/scheduling/api/subscribe-scheduling-state.js';
import { testSchedulingByTicketIds } from '@nsc/workflows-dell/scheduling/api/test-scheduling-by-ticket-ids.js';
import { html, nothing } from 'lit';

type SchedulingHistoryItem = {
  historyKey: string;
  phase?: string | null;
  message?: string | null;
  error?: string | null;
  updatedAt?: string | null;
  ticketsViewedCount?: number | null;
  ticketsToScheduleCount?: number | null;
  ticketsWithFECount?: number | null;
  availableFECount?: number | null;
  routesCount?: number | null;
};
type SchedulingCardData = Omit<SchedulingHistoryItem, 'historyKey'>;

const terminalPhases = [
  'idle',
  'completed',
  'error',
  'route_planned',
  'outbound_pudo_done',
  'send_email_part_exchange_done',
] as const;
const successPhases = ['completed', 'route_planned', 'outbound_pudo_done', 'send_email_part_exchange_done'] as const;
let latestResultLogsRef: string[] = [];
let latestSchedulingHistoryRef: SchedulingHistoryItem[] = [];

export const SchedulingDebugPage = stateful(() => {
  const [ticketIdsInput, setTicketIdsInput] = useState('');
  const [parsedTicketIds, setParsedTicketIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultLogs, setResultLogs] = useState<string[]>([]);
  const [schedulingHistory, setSchedulingHistory] = useState<SchedulingHistoryItem[]>([]);
  const [lastSchedulingHistoryKey, setLastSchedulingHistoryKey] = useState<string | null>(null);
  const [writeToOTRS, setWriteToOTRS] = useState(false);
  const [isRunningFullSimulation, setIsRunningFullSimulation] = useState(false);
  const [isRunningFullScheduling, setIsRunningFullScheduling] = useState(false);
  const [isRunningManualChecksum, setIsRunningManualChecksum] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRunFullSchedulingConfirmDialog, setShowRunFullSchedulingConfirmDialog] = useState(false);
  const [showStartSchedulingConfirmDialog, setShowStartSchedulingConfirmDialog] = useState(false);
  const [showOTRSConfirmDialog, setShowOTRSConfirmDialog] = useState(false);
  const [lastSeenActivePhase, setLastSeenActivePhase] = useState<string | null>(null);

  const schedulingState = useObservable(
    () =>
      subscribeSchedulingState()({
        phase: 1,
        message: 1,
        ticketsViewedCount: 1,
        ticketsToScheduleCount: 1,
        ticketsWithFECount: 1,
        availableFECount: 1,
        routesCount: 1,
        error: 1,
        updatedAt: 1,
      }),
    [],
  );

  const isSchedulingActive = schedulingState?.phase && !terminalPhases.includes(schedulingState.phase as any);
  latestResultLogsRef = resultLogs;
  latestSchedulingHistoryRef = schedulingHistory;
  const appendResultLog = (message: string) => {
    const timestamp = new Date().toLocaleString();
    const nextLogs = [`[${timestamp}] ${message}`, ...latestResultLogsRef];
    latestResultLogsRef = nextLogs;
    setResultLogs(nextLogs);
  };
  const clearResultLogs = () => {
    latestResultLogsRef = [];
    setResultLogs([]);
  };
  const clearSchedulingHistory = async () => {
    const currentHistoryKey =
      schedulingState?.phase != null
        ? `${schedulingState.updatedAt ?? ''}|${schedulingState.phase}|${schedulingState.message ?? ''}|${schedulingState.error ?? ''}`
        : null;
    latestSchedulingHistoryRef = [];
    setSchedulingHistory([]);
    setLastSchedulingHistoryKey(currentHistoryKey);
    try {
      await clearSchedulingState();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear scheduling state');
    }
  };
  const getPhaseLabel = (phase?: string | null) => {
    return phase === 'accumulating_tickets'
      ? 'Accumulating Tickets'
      : phase === 'tickets_accumulated'
        ? 'Tickets Accumulated'
        : phase === 'fetching_available_fes'
          ? 'Fetching Field Engineers'
          : phase === 'planning_route'
            ? 'Planning Routes'
            : phase === 'route_planned'
              ? 'Routes Planned'
              : phase === 'writing_to_otrs'
                ? 'Writing to OTRS'
                : phase === 'completed'
                  ? 'Completed'
                  : phase === 'error'
                    ? 'Error'
                    : phase === 'outbound_pudo_scanning'
                      ? 'Outbound PUDO: scanning'
                      : phase === 'outbound_pudo_selecting_fe'
                        ? 'Outbound PUDO: selecting FE'
                        : phase === 'outbound_pudo_writing_actions'
                          ? 'Outbound PUDO: writing'
                          : phase === 'outbound_pudo_done'
                            ? 'Outbound PUDO: done'
                            : phase === 'send_email_part_exchange_started'
                              ? 'Send email part exchange: started'
                              : phase === 'send_email_part_exchange_done'
                                ? 'Send email part exchange: done'
                                : phase === 'send_email_part_exchange_error'
                                  ? 'Send email part exchange: error'
                                  : phase;
  };
  const renderSchedulingStateStats = (item: SchedulingCardData, compact: boolean) => {
    const numberClass = compact ? 'font-bold text-sm' : 'font-bold text-lg';
    const labelClass = compact ? 'text-xs text-base-content/60' : 'text-base-content/60';
    const containerClass = compact ? 'flex gap-4 text-xs' : 'flex gap-6 text-sm';
    return html`
      <div class="${containerClass}">
        ${item.ticketsViewedCount != null
          ? html`
              <div class="text-center">
                <div class="${numberClass}">${item.ticketsViewedCount}</div>
                <div class="${labelClass}">Viewed</div>
              </div>
            `
          : nothing}
        ${item.ticketsToScheduleCount != null
          ? html`
              <div class="text-center">
                <div class="${numberClass} text-primary">${item.ticketsToScheduleCount}</div>
                <div class="${labelClass}">To Schedule</div>
              </div>
            `
          : nothing}
        ${item.ticketsWithFECount != null
          ? html`
              <div class="text-center">
                <div class="${numberClass} text-secondary">${item.ticketsWithFECount}</div>
                <div class="${labelClass}">With FE</div>
              </div>
            `
          : nothing}
        ${item.availableFECount != null
          ? html`
              <div class="text-center">
                <div class="${numberClass} text-accent">${item.availableFECount}</div>
                <div class="${labelClass}">Available FEs</div>
              </div>
            `
          : nothing}
        ${item.routesCount != null
          ? html`
              <div class="text-center">
                <div class="${numberClass} text-success">${item.routesCount}</div>
                <div class="${labelClass}">Routes</div>
              </div>
            `
          : nothing}
      </div>
    `;
  };
  const renderSchedulingStateCard = (
    item: SchedulingCardData,
    options: { compact?: boolean; showUpdatedAt?: boolean; forceActive?: boolean } = {},
  ) => {
    const compact = options.compact ?? false;
    const isActive = options.forceActive ?? (item.phase && !terminalPhases.includes(item.phase as any));
    const borderClass =
      item.phase === 'error'
        ? 'border-error'
        : successPhases.includes(item.phase as any)
          ? 'border-success'
          : 'border-info';
    return html`
      <div
        class="card bg-base-100 rounded-${compact ? 'lg' : 'xl'} border ${borderClass} ${compact ? '' : 'shadow-xl'}"
      >
        <div class="card-body ${compact ? 'py-3 px-4' : 'py-4'}">
          <div class="flex items-center gap-4">
            ${isActive
              ? html`<span class="loading loading-spinner ${compact ? 'loading-sm' : 'loading-md'} text-info"></span>`
              : item.phase === 'error'
                ? html`<span class="text-error ${compact ? 'text-xl' : 'text-2xl'}">✕</span>`
                : html`<span class="text-success ${compact ? 'text-xl' : 'text-2xl'}">✓</span>`}
            <div class="flex-1">
              <div class="font-semibold">${getPhaseLabel(item.phase) ?? 'Unknown phase'}</div>
              <div class="text-sm text-base-content/70">${item.message ?? '-'}</div>
              ${item.error ? html`<div class="text-sm text-error mt-1">${item.error}</div>` : nothing}
              ${options.showUpdatedAt
                ? html`
                    <div class="text-xs text-base-content/50 mt-1">
                      ${item.updatedAt ? new Date(item.updatedAt).toLocaleString() : 'Unknown time'}
                    </div>
                  `
                : nothing}
            </div>
            ${renderSchedulingStateStats(item, compact)}
          </div>
        </div>
      </div>
    `;
  };

  // Track when scheduling becomes active so we can show completed/error states only for runs we've witnessed
  const hasSeenActiveScheduling = lastSeenActivePhase !== null;
  useEffect(() => {
    if (isSchedulingActive && lastSeenActivePhase !== schedulingState?.phase) {
      setLastSeenActivePhase(schedulingState?.phase ?? null);
    } else if (schedulingState?.phase === 'idle' && hasSeenActiveScheduling) {
      setLastSeenActivePhase(null);
    }
  }, [isSchedulingActive, schedulingState?.phase]);
  useEffect(() => {
    if (!schedulingState?.phase) return;
    const isInitialIdle =
      schedulingState.phase === 'idle' && !hasSeenActiveScheduling && schedulingHistory.length === 0;
    if (isInitialIdle) return;
    const historyKey = `${schedulingState.updatedAt ?? ''}|${schedulingState.phase}|${schedulingState.message ?? ''}|${schedulingState.error ?? ''}`;
    if (historyKey === lastSchedulingHistoryKey) return;
    setLastSchedulingHistoryKey(historyKey);
    const nextHistory = [
      {
        historyKey,
        phase: schedulingState.phase,
        message: schedulingState.message,
        error: schedulingState.error,
        updatedAt: schedulingState.updatedAt ? String(schedulingState.updatedAt) : undefined,
        ticketsViewedCount: schedulingState.ticketsViewedCount,
        ticketsToScheduleCount: schedulingState.ticketsToScheduleCount,
        ticketsWithFECount: schedulingState.ticketsWithFECount,
        availableFECount: schedulingState.availableFECount,
        routesCount: schedulingState.routesCount,
      },
      ...latestSchedulingHistoryRef,
    ];
    latestSchedulingHistoryRef = nextHistory;
    setSchedulingHistory(nextHistory);
  }, [
    schedulingState?.updatedAt,
    schedulingState?.phase,
    schedulingState?.message,
    schedulingState?.error,
    schedulingState?.ticketsViewedCount,
    schedulingState?.ticketsToScheduleCount,
    schedulingState?.ticketsWithFECount,
    schedulingState?.availableFECount,
    schedulingState?.routesCount,
    hasSeenActiveScheduling,
    lastSchedulingHistoryKey,
  ]);

  const shouldShowIndicator =
    isSchedulingActive ||
    (hasSeenActiveScheduling &&
      (schedulingState?.phase === 'error' || successPhases.includes(schedulingState?.phase as any)));
  const currentSchedulingHistoryKey =
    schedulingState?.phase != null
      ? `${schedulingState.updatedAt ?? ''}|${schedulingState.phase}|${schedulingState.message ?? ''}|${schedulingState.error ?? ''}`
      : null;

  const parseTicketIds = (input: string): string[] => {
    return input
      .split(/[\n,;]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
  };

  const handleInputChange = (e: Event) => {
    const value = (e.target as HTMLTextAreaElement).value;
    setTicketIdsInput(value);
    setParsedTicketIds(parseTicketIds(value));
    setError(null);
  };

  const handleStartScheduling = async () => {
    if (parsedTicketIds.length === 0) {
      setError('Please enter at least one ticket ID');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const plannedRoutes = await testSchedulingByTicketIds(parsedTicketIds, writeToOTRS)();

      console.log('Starting manual scheduling for tickets:', parsedTicketIds);
      appendResultLog(
        `Scheduling completed for ${parsedTicketIds.length} ticket(s). Planned routes: ${plannedRoutes}${writeToOTRS ? ' (written to OTRS)' : ''}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while starting scheduling');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    setTicketIdsInput('');
    setParsedTicketIds([]);
    setError(null);
    setWriteToOTRS(false);
  };

  const handleRunFullSimulation = async () => {
    setShowConfirmDialog(false);
    setIsRunningFullSimulation(true);
    setError(null);

    try {
      const routeResult = await runFullSimulatedScheduling()({ routes: { _id: 1 } });
      const routeCount = routeResult?.routes?.length ?? 0;
      appendResultLog(
        `Full simulation completed. Planned ${routeCount} route(s). Results saved to database (not written to OTRS).`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during full simulation');
    } finally {
      setIsRunningFullSimulation(false);
    }
  };

  const handleRunFullScheduling = async () => {
    setShowRunFullSchedulingConfirmDialog(false);
    setIsRunningFullScheduling(true);
    setError(null);

    try {
      const routeResult = await runFullScheduling()({ routes: { _id: 1 } });
      const routeCount = routeResult?.routes?.length ?? 0;
      appendResultLog(`Full scheduling completed. Planned ${routeCount} route(s). Results written to OTRS.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during full scheduling');
    } finally {
      setIsRunningFullScheduling(false);
    }
  };

  const handleRunManualChecksum = async () => {
    setIsRunningManualChecksum(true);
    setError(null);

    try {
      await runManualChecksum()();
      await waitForManualChecksumToFinish()();
      appendResultLog(`Checksum calculation completed. Generated route(s). Results saved to database.`);
      const outboundPudoPickupSummary = await runOutboundPudoPickup()();
      appendResultLog(`Outbound PUDO pickup summary: ${outboundPudoPickupSummary}`);
      const sendEmailPartExchangeSummary = await runSendEmailPartExchange()();
      appendResultLog(`Send email part exchange summary: ${sendEmailPartExchangeSummary}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during checksum calculation');
    } finally {
      setIsRunningManualChecksum(false);
    }
  };

  return html`
    <div class="max-w-4xl mx-auto p-8">
      <div class="flex justify-between items-start mb-6">
        <div>
          <h2 class="text-2xl font-bold mb-2">Manual Scheduling</h2>
          <p class="text-sm text-base-content/70">
            Manually trigger the scheduling process for specific tickets or run full planning/scheduling.
          </p>
        </div>
        <div class="flex gap-2 flex-wrap justify-end">
          <button
            class="btn btn-secondary"
            @click=${() => setShowConfirmDialog(true)}
            ?disabled=${isRunningFullSimulation || isRunningFullScheduling || isRunningManualChecksum || isProcessing}
          >
            ${isRunningFullSimulation
              ? html`<span class="loading loading-spinner loading-sm"></span> Running...`
              : html`Run Full Planning`}
          </button>
          <button
            class="btn btn-warning"
            @click=${() => setShowRunFullSchedulingConfirmDialog(true)}
            ?disabled=${isRunningFullSimulation || isRunningFullScheduling || isRunningManualChecksum || isProcessing}
          >
            ${isRunningFullScheduling
              ? html`<span class="loading loading-spinner loading-sm"></span> Running...`
              : html`Run Full Scheduling`}
          </button>
          <button
            class="btn btn-accent"
            @click=${handleRunManualChecksum}
            ?disabled=${isRunningFullSimulation || isRunningFullScheduling || isRunningManualChecksum || isProcessing}
          >
            ${isRunningManualChecksum
              ? html`<span class="loading loading-spinner loading-sm"></span> Running...`
              : html`Daily Scheduling Finished`}
          </button>
        </div>

        <dialog class="modal" ?open=${showConfirmDialog}>
          <div class="modal-box">
            <h3 class="font-bold text-lg">Run Full Planning</h3>
            <p class="py-4">
              This will run full route planning for all eligible tickets.
              <br /><br />
              <strong class="text-warning">This may take several minutes.</strong>
            </p>
            <div class="alert alert-warning">
              <span> Running planning will incur costs. Only continue if you understand and accept the costs. </span>
            </div>
            <div class="modal-action">
              <button class="btn btn-ghost" @click=${() => setShowConfirmDialog(false)}>Cancel</button>
              <button class="btn btn-secondary" @click=${handleRunFullSimulation}>Continue</button>
            </div>
          </div>
          <form method="dialog" class="modal-backdrop">
            <button @click=${() => setShowConfirmDialog(false)}>close</button>
          </form>
        </dialog>

        <dialog class="modal" ?open=${showRunFullSchedulingConfirmDialog}>
          <div class="modal-box">
            <h3 class="font-bold text-lg text-warning">Run Full Scheduling</h3>
            <p class="py-4">
              This will run full scheduling for all eligible tickets.
              <br /><br />
              <strong class="text-warning">This may take several minutes.</strong>
            </p>
            <div class="alert alert-warning">
              <span> Running scheduling will incur costs. Only continue if you understand and accept the costs. </span>
            </div>
            <div class="alert alert-warning mt-3">
              <span>
                <strong>This will write scheduling results back to production OTRS.</strong>
              </span>
            </div>
            <div class="modal-action">
              <button class="btn btn-ghost" @click=${() => setShowRunFullSchedulingConfirmDialog(false)}>Cancel</button>
              <button class="btn btn-warning" @click=${handleRunFullScheduling}>Continue</button>
            </div>
          </div>
          <form method="dialog" class="modal-backdrop">
            <button @click=${() => setShowRunFullSchedulingConfirmDialog(false)}>close</button>
          </form>
        </dialog>
      </div>

      ${shouldShowIndicator && schedulingHistory.length > 0
        ? html`
            <div class="mb-6 card bg-base-100 shadow-xl rounded-xl border border-neutral/10">
              <div class="card-body py-4">
                <div class="flex items-center justify-between gap-2 mb-2">
                  <h3 class="font-semibold">Scheduling State History</h3>
                  <button class="btn btn-xs btn-ghost" @click=${clearSchedulingHistory}>Clear</button>
                </div>
                <div class="space-y-2 max-h-56 overflow-y-auto">
                  ${schedulingHistory.map(item => {
                    const isCurrentRunningItem =
                      Boolean(isSchedulingActive) &&
                      currentSchedulingHistoryKey != null &&
                      item.historyKey === currentSchedulingHistoryKey;
                    return renderSchedulingStateCard(item, {
                      compact: true,
                      showUpdatedAt: true,
                      forceActive: isCurrentRunningItem,
                    });
                  })}
                </div>
              </div>
            </div>
          `
        : nothing}

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Input Section -->
        <section class="card bg-base-100 shadow-xl rounded-xl border border-neutral/10">
          <div class="card-body">
            <h3 class="card-title text-xl font-bold mb-4">Ticket IDs</h3>
            <p class="text-sm text-base-content/60 mb-4">
              Enter ticket IDs separated by commas, semicolons, or new lines.
            </p>
            <textarea
              class="textarea textarea-bordered w-full h-48 font-mono text-sm"
              placeholder="Enter ticket IDs here...&#10;e.g.&#10;12345&#10;67890&#10;or: 12345, 67890, 11111"
              .value=${ticketIdsInput}
              @input=${handleInputChange}
              ?disabled=${isProcessing || isRunningFullSimulation || isRunningFullScheduling || isRunningManualChecksum}
            ></textarea>

            <div class="form-control mt-4">
              <label class="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  class="checkbox checkbox-primary"
                  .checked=${writeToOTRS}
                  @change=${(e: Event) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    if (checked) {
                      (e.target as HTMLInputElement).checked = false;
                      setShowOTRSConfirmDialog(true);
                    } else {
                      setWriteToOTRS(false);
                    }
                  }}
                  ?disabled=${isProcessing ||
                  isRunningFullSimulation ||
                  isRunningFullScheduling ||
                  isRunningManualChecksum}
                />
                <span class="label-text">Write results to OTRS</span>
              </label>
            </div>

            <dialog class="modal" ?open=${showOTRSConfirmDialog}>
              <div class="modal-box">
                <h3 class="font-bold text-lg text-warning">Write to Production OTRS</h3>
                <p class="py-4">
                  Are you sure you want to enable this option?
                  <br /><br />
                  <strong class="text-error"
                    >This will write scheduling results directly to the production OTRS instance.</strong
                  >
                </p>
                <div class="alert alert-warning">
                  <span>
                    Running scheduling will incur costs. Only continue if you understand and accept the costs.
                  </span>
                </div>
                <div class="modal-action">
                  <button class="btn btn-ghost" @click=${() => setShowOTRSConfirmDialog(false)}>Cancel</button>
                  <button
                    class="btn btn-warning"
                    @click=${() => {
                      setWriteToOTRS(true);
                      setShowOTRSConfirmDialog(false);
                    }}
                  >
                    Yes, I'm sure
                  </button>
                </div>
              </div>
              <form method="dialog" class="modal-backdrop">
                <button @click=${() => setShowOTRSConfirmDialog(false)}>close</button>
              </form>
            </dialog>

            <dialog class="modal" ?open=${showStartSchedulingConfirmDialog}>
              <div class="modal-box">
                <h3 class="font-bold text-lg">Start Scheduling</h3>
                <p class="py-4">This will run scheduling for <strong>${parsedTicketIds.length}</strong> ticket(s).</p>
                <div class="alert alert-warning">
                  <span
                    >Running ${writeToOTRS ? 'scheduling' : 'planning'} will incur costs. Only continue if you
                    understand and accept the costs.</span
                  >
                </div>
                <div class="modal-action">
                  <button class="btn btn-ghost" @click=${() => setShowStartSchedulingConfirmDialog(false)}>
                    Cancel
                  </button>
                  <button
                    class="btn btn-primary"
                    @click=${async () => {
                      setShowStartSchedulingConfirmDialog(false);
                      await handleStartScheduling();
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
              <form method="dialog" class="modal-backdrop">
                <button @click=${() => setShowStartSchedulingConfirmDialog(false)}>close</button>
              </form>
            </dialog>

            <div class="flex gap-2 mt-4">
              <button
                class="btn btn-primary flex-1"
                @click=${() => setShowStartSchedulingConfirmDialog(true)}
                ?disabled=${isProcessing ||
                isRunningFullSimulation ||
                isRunningFullScheduling ||
                isRunningManualChecksum ||
                parsedTicketIds.length === 0}
              >
                ${isProcessing
                  ? html`<span class="loading loading-spinner loading-sm"></span> Processing...`
                  : html`Start ${writeToOTRS ? 'Scheduling' : 'Planning'}`}
              </button>
              <button
                class="btn btn-ghost"
                @click=${handleClear}
                ?disabled=${isProcessing ||
                isRunningFullSimulation ||
                isRunningFullScheduling ||
                isRunningManualChecksum ||
                ticketIdsInput.length === 0}
              >
                Clear
              </button>
            </div>

            ${error
              ? html`
                  <div class="alert alert-error mt-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="stroke-current shrink-0 h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>${error}</span>
                  </div>
                `
              : nothing}
          </div>
        </section>

        <!-- Preview Section -->
        <section class="card bg-base-100 shadow-xl rounded-xl border border-neutral/10">
          <div class="card-body">
            <h3 class="card-title text-xl font-bold mb-4">
              Parsed Tickets
              <span class="badge badge-primary">${parsedTicketIds.length}</span>
            </h3>
            <p class="text-sm text-base-content/60 mb-4">Preview of ticket IDs that will be scheduled.</p>

            ${parsedTicketIds.length > 0
              ? html`
                  <div class="overflow-y-auto max-h-64 border border-base-200 rounded-lg">
                    <table class="table table-sm w-full">
                      <thead class="sticky top-0 bg-base-200">
                        <tr>
                          <th class="w-12">#</th>
                          <th>Ticket ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${parsedTicketIds.map(
                          (id, index) => html`
                            <tr class="hover">
                              <td class="text-base-content/60">${index + 1}</td>
                              <td class="font-mono">${id}</td>
                            </tr>
                          `,
                        )}
                      </tbody>
                    </table>
                  </div>
                `
              : html`
                  <div class="flex flex-col items-center justify-center h-48 text-base-content/40">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-12 w-12 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    <span>No ticket IDs entered</span>
                  </div>
                `}
          </div>
        </section>
      </div>
      ${resultLogs.length > 0
        ? html`
            <div class="mt-8 card bg-base-100 shadow-xl rounded-xl border border-neutral/10">
              <div class="card-body py-4">
                <div class="flex items-center justify-between gap-2 mb-2">
                  <h3 class="font-semibold">Result Logs</h3>
                  <button class="btn btn-xs btn-ghost" @click=${clearResultLogs}>Clear</button>
                </div>
                <div class="max-h-56 overflow-y-auto space-y-2">
                  ${resultLogs.map(
                    item => html`
                      <div class="card bg-base-100 rounded-lg border border-success/30">
                        <div class="card-body py-3 px-4">
                            <div class="flex items-start gap-3">
                              <span class="text-success text-xl">✓</span>
                              <span class="text-sm whitespace-pre-line">${item}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>
          `
        : nothing}
    </div>
  `;
});
